import { Task } from "@freeconvert/freeconvert-node/dist/types";
import { notNullish } from "client/hooks/utils/notNullish";
import { get } from "client/services/Backend/get";
import { formatBytes } from "client/utils/formatBytes";
import { getFCUrl } from "client/utils/getFCUrl";
import { getRandomId } from "client/utils/getRandomId";
import { BulkResizeImage } from "store/features/bulkResize/types";
import { useAppDispatch } from "store/store";
import { addImage } from "store/features/bulkResize/actions";
import { getUrlExtension } from "client/utils/getUrlExtension";
import { getFileNameFromTask } from "client/utils/getFileNameFromTask";

export const useUrlTaskIds = () => {
    const dispatch = useAppDispatch();
    const handleUrlTaskIds = async (taskIds: string[]) => (await getTasks(taskIds)).forEach(createNewImage);

    const getTasks = async (taskIds: string[]) =>
        (
            await Promise.all(
                taskIds.map(async (taskId) => {
                    const task = await getTaskData(taskId);
                    if (!task || task.status !== "completed" || !task.result) return;
                    return task;
                }),
            )
        ).filter(notNullish);

    const getTaskData = async (taskId: string): Promise<Task | null> => {
        try {
            const { error, result } = await get<Task>(`${process.env.NEXT_PUBLIC_BE_URL}/tasks/${taskId}`);
            return error ? null : result;
        } catch (e) {
            return null;
        }
    };

    const createNewImage = (task: Task) => {
        if (!task.result) return;
        const { url, size } = task.result;
        const name = getFileNameFromTask(task);
        const src = getFCUrl(url?.toString() || "");
        const image: BulkResizeImage = {
            id: getRandomId(),
            initialDimensions: {
                width: 0,
                height: 0,
            },
            original: {
                width: 0,
                height: 0,
                size: formatBytes(size || 0),
                src,
            },
            preview: {
                width: 0,
                height: 0,
                size: formatBytes(size || 0),
                src,
            },
            title: name,
            format: getUrlExtension(url?.toString() || ""),
            createdAt: new Date(task.createdAt),
            state: "imported",
            importTask: task,
            importing: {
                progress: 100,
            },
            crop: {
                height: 0,
                width: 0,
                top: 0,
                left: 0,
                ratio: "freeform",
                cropBox: null,
                customRatioValues: {
                    x: 1,
                    y: 1,
                },
            },
            flip: {
                horizontal: false,
                vertical: false,
            },
            rotate: {
                rotation: 0,
                straighten: 0,
            },
            operations: [],
        };
        dispatch(addImage(image));
    };

    return { handleUrlTaskIds };
};
